// The five clients. Plain data, no JSX.
//
// slug is the URL segment (/developers/codex) and must match the client topics
// in Web::DevelopersController::TOPICS. kind is McpInstallPrompt's kind, used
// to pick the install prompt.
//
// {MCP_URL}, {API_BASE} and <pt_ token> are placeholders filled at render time:
// this file can't know the request host or a freshly generated token.
export type ClientKind = 'claude_code' | 'claude_desktop' | 'codex' | 'workbuddy' | 'other';

export type ClientMeta = {
  kind: ClientKind;
  slug: string;
  label: string;
  // Second line on the nav and the start page cards: what connecting takes.
  how: string;
  // The sentence under the page title: which way this client connects.
  route: string;
  manual?: { intro: string; code: string; note?: string };
  verify?: string;
  gotcha?: string;
};

export const CLIENTS: ClientMeta[] = [
  {
    kind: 'claude_code',
    slug: 'claude-code',
    label: 'Claude Code',
    how: '一条命令',
    route: '粘 token，一条命令接上，所有项目共用。Desktop 的 Code tab 读同一份配置，接一次两边都能用。',
    manual: {
      intro: '在终端里跑一次：',
      code: 'claude mcp add --transport http --scope user asaptodo {MCP_URL} --header "Authorization: Bearer <pt_ token>"',
      note: '--scope user 让所有项目都能用，不必每个仓库配一遍。',
    },
    verify: '跑 claude mcp list，asaptodo 那行应该是「✔ Connected」。',
    gotcha:
      '要是显示「tools fetch failed — missing required resultType」，说明这台 asaptodo 的服务端版本太旧，升级即可 —— 不是你配错了。',
  },
  {
    kind: 'claude_desktop',
    slug: 'claude-desktop',
    label: 'Claude Desktop',
    how: '仅 Code tab',
    route: 'Code tab 能接，和 Claude Code 共用配置；聊天里的自定义连接器接不上。',
  },
  {
    kind: 'codex',
    slug: 'codex',
    label: 'Codex CLI',
    how: '改 config.toml',
    route: '粘 token。token 走环境变量，不落进配置文件。',
    manual: {
      intro: '把这一节并进 ~/.codex/config.toml（文件已存在时只加这一节，别整体覆盖）：',
      code: '[mcp_servers.asaptodo]\nurl = "{MCP_URL}"\nbearer_token_env_var = "ASAPTODO_TOKEN"',
      note: 'Codex 读环境变量而不是把 token 写进配置文件，所以还要 export ASAPTODO_TOKEN=<pt_ token>。',
    },
    verify: '重启 Codex，让它调一次 task_list。',
    gotcha:
      '连不上时先确认 ASAPTODO_TOKEN 在当前 shell 里真的有值 —— 拿不到它的表现是连不上，不是报鉴权错，很容易往错的方向查。',
  },
  {
    kind: 'workbuddy',
    slug: 'workbuddy',
    label: 'WorkBuddy',
    how: '改 mcp.json',
    route: '粘 token。写进一份 JSON 配置文件。',
    manual: {
      intro: '把 asaptodo 这一项并进 ~/.workbuddy/mcp.json（只加这一项，别整体覆盖）：',
      code: '{\n  "mcpServers": {\n    "asaptodo": {\n      "type": "http",\n      "url": "{MCP_URL}",\n      "headers": { "Authorization": "Bearer <pt_ token>" }\n    }\n  }\n}',
      note: '不想动文件就走界面：侧边栏「插件 → MCP 服务器 → 配置 MCP」，把上面这段贴进去。',
    },
    verify: '重启 WorkBuddy，让它调一次 task_list。',
    gotcha:
      'WorkBuddy 不读环境变量，token 是明文躺在文件里的。放进 <项目目录>/.workbuddy/mcp.json 只对那个项目生效 —— 那一份记得进 .gitignore。',
  },
  {
    kind: 'other',
    slug: 'scripts',
    label: '其他 / 脚本',
    how: '直接调 REST',
    route: '不走 MCP，直接调 REST。',
    manual: {
      intro: '先把 token 放进环境变量，再调一次：',
      code: 'export ASAPTODO_TOKEN=<pt_ token>\ncurl -s "{API_BASE}/tasks?view=today" \\\n  -H "Authorization: Bearer $ASAPTODO_TOKEN"',
    },
    verify: '能列出今天的任务就说明通了。',
    gotcha:
      '401 是 token 不对；404 可能是 :id 写成了别人的任务 —— 找不到与不属于你的一律回「不存在」，这是有意的。',
  },
];

export const clientBySlug = (slug: string | null): ClientMeta | undefined =>
  CLIENTS.find((c) => c.slug === slug);

// Fills the placeholders. The token exists only in the response right after
// it was generated.
export const fillPlaceholders = (
  text: string,
  urls: { mcpUrl: string; apiBase: string },
  token?: string,
): string => {
  const filled = text.replace(/\{MCP_URL\}/g, urls.mcpUrl).replace(/\{API_BASE\}/g, urls.apiBase);
  return token ? filled.replace(/<pt_ token>/g, token) : filled;
};
